import { METRICS_LIST, type MetricsList } from '@/features/locations/constants';

function withoutKeys(metrics: readonly MetricsList[], ...keys: MetricsList[]): MetricsList[] {
  return metrics.filter((m) => !keys.includes(m));
}

/** Toggle a simple, independent metric on or off. */
function toggleMetricSelection(
  metrics: readonly MetricsList[],
  key: MetricsList,
  checked: boolean,
): MetricsList[] {
  return checked ? [...withoutKeys(metrics, key), key] : withoutKeys(metrics, key);
}

/**
 * Toggle the demand forecast metric. Forecast requires captured AADT, so selecting it
 * also selects captured AADT; it is mutually exclusive with zone forecast.
 */
export function toggleForecastSelection(
  metrics: readonly MetricsList[],
  checked: boolean,
): MetricsList[] {
  if (!checked) {
    return withoutKeys(metrics, METRICS_LIST.FORECAST);
  }
  return [
    ...withoutKeys(
      metrics,
      METRICS_LIST.FORECAST,
      METRICS_LIST.ZONE_FORECAST,
      METRICS_LIST.CAPTURED_AADT,
    ),
    METRICS_LIST.CAPTURED_AADT,
    METRICS_LIST.FORECAST,
  ];
}

/**
 * Toggle the zone forecast metric. It is mutually exclusive with demand forecast;
 * captured AADT is left untouched (the two can coexist).
 */
export function toggleZoneForecastSelection(
  metrics: readonly MetricsList[],
  checked: boolean,
): MetricsList[] {
  if (!checked) {
    return withoutKeys(metrics, METRICS_LIST.ZONE_FORECAST);
  }
  return [
    ...withoutKeys(metrics, METRICS_LIST.ZONE_FORECAST, METRICS_LIST.FORECAST),
    METRICS_LIST.ZONE_FORECAST,
  ];
}

/**
 * Toggle captured AADT. It cannot be deselected while demand forecast is selected,
 * since forecast requires it.
 */
export function toggleCapturedAadtSelection(
  metrics: readonly MetricsList[],
  checked: boolean,
): MetricsList[] {
  if (!checked && metrics.includes(METRICS_LIST.FORECAST)) {
    return [...metrics];
  }
  return toggleMetricSelection(metrics, METRICS_LIST.CAPTURED_AADT, checked);
}
